import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import {
    VirtualizedTableOnItemValueChange,
    VirtualizedTableOnItemsValueChange
} from 'components/VirtualizedTable/types';
import {
    VirtualizedTableColumnKey,
    VirtualizedTableColumnWidth,
    VirtualizedTableColumns,
    VirtualizedTableItem
} from 'services/virtualizedTable/types';
import { ColumnWidthTableId } from 'services/columnWidths/types';
import { EntityTypesProps } from 'services/entityTypes/types';
import {
    makeSelectIsLastDepth,
    makeSelectEntityTypeName,
    makeSelectEntityTypePluralName
} from 'services/entityTypes/selectors';
import { setColumnWidth } from 'services/columnWidths/actions';
import {
    updateEntity,
    updateEntitiesValue,
    updateEntityValue
} from 'services/entities/actions';
import { Entity } from 'services/entities/types';
import VirtualizedTable from 'components/VirtualizedTable';
// @ts-ignore
import { selectCurrentSeasonId } from 'services/currentSeasonId/selectors';

import {
    makeSelectEntitiesTableColumns,
    makeSelectEntitiesArrayItems,
    selectIsWritable
} from './selectors';

export interface OwnProps extends EntityTypesProps {
    tableId: ColumnWidthTableId;
}

interface ConnectedProps {
    columns: VirtualizedTableColumns;
    items: VirtualizedTableItem[];
    isWritable?: ReturnType<typeof selectIsWritable>;
    entityTypeName?: ReturnType<ReturnType<typeof makeSelectEntityTypeName>>;
    entityTypePluralName?: ReturnType<
        ReturnType<typeof makeSelectEntityTypePluralName>
    >;
    currentSeasonId: ReturnType<typeof selectCurrentSeasonId>;
    isLastDepth?: ReturnType<ReturnType<typeof makeSelectIsLastDepth>>;
}

interface DispatchProps {
    setColumnWidth: typeof setColumnWidth;
    updateEntity: typeof updateEntity;
    updateEntitiesValue: typeof updateEntitiesValue;
    updateEntityValue: typeof updateEntityValue;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class EntitiesVirtualizedTableComponent extends React.Component<Props> {
    shouldComponentUpdate(nextProps: Props): boolean {
        return !isEqual(this.props, nextProps);
    }

    handleColumnResized = (
        columnKey: VirtualizedTableColumnKey,
        columnWidth: VirtualizedTableColumnWidth
    ): void => {
        const { tableId, ...props } = this.props;
        props.setColumnWidth(tableId, columnKey, columnWidth);
    };

    handleCreate = (item: VirtualizedTableItem): void => {
        const { entityTypeId, currentSeasonId, ...props } = this.props;

        const entity = {
            ...item,
            entityTypeId,
            seasonId: currentSeasonId,
            removals: item.removals || []
        };

        props.updateEntity(entity as Entity);
    };

    handleItemUpdate = ({ key, ...item }: VirtualizedTableItem): void => {
        const { entityTypeId, currentSeasonId, ...props } = this.props;
        const entity = {
            ...item,
            uuid: key,
            entityTypeId,
            seasonId: currentSeasonId,
            removals: item.removals || []
        };
        props.updateEntity(entity as Entity);
    };

    handleItemValueChange: VirtualizedTableOnItemValueChange = (
        itemKey,
        columnKey,
        itemValue
    ) => {
        const { props } = this;

        props.updateEntityValue({
            entityUuid: itemKey,
            field: columnKey,
            value: itemValue
        });
    };

    handleItemsValueChange: VirtualizedTableOnItemsValueChange = (
        itemKeys,
        columnKey,
        itemValue
    ) => {
        const { props } = this;

        props.updateEntitiesValue({
            entityUuids: itemKeys,
            field: columnKey,
            value: itemValue
        });
    };

    handleHiddenFormFields = (item: VirtualizedTableItem): string[] => {
        const { isLastDepth } = this.props;

        let hiddenFormFields: string[] = [];

        if (isLastDepth) {
            hiddenFormFields = [
                ...hiddenFormFields,
                'removalsTotalArea',
                'removalsTotalTrees'
            ];
        }

        return hiddenFormFields;
    };

    renderVirtualizedTable = ({ height, width }: Size): React.ReactElement => {
        const {
            tableId,
            columns,
            items,
            isWritable,
            entityTypeName,
            entityTypePluralName,
            isLastDepth
        } = this.props;

        return (
            <VirtualizedTable
                id={tableId}
                label={entityTypeName}
                labelPlural={entityTypePluralName}
                height={height}
                width={width}
                columns={columns}
                items={items}
                onColumnResized={this.handleColumnResized}
                onItemValueChange={this.handleItemValueChange}
                onItemsValueChange={this.handleItemsValueChange}
                selectable={isWritable}
                editable={isWritable}
                enableBulkEditing={isWritable}
                enableCreating
                enableItemEditing={isWritable}
                enableFooter={isLastDepth}
                onCreate={this.handleCreate}
                onItemUpdate={this.handleItemUpdate}
                hiddenFormFields={this.handleHiddenFormFields}
            />
        );
    };

    render(): React.ReactElement {
        return (
            <AutoSizer>
                {(size: Size): React.ReactElement =>
                    this.renderVirtualizedTable(size)
                }
            </AutoSizer>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    columns: makeSelectEntitiesTableColumns(),
    items: makeSelectEntitiesArrayItems(),
    isWritable: selectIsWritable,
    entityTypeName: makeSelectEntityTypeName(),
    entityTypePluralName: makeSelectEntityTypePluralName(),
    currentSeasonId: selectCurrentSeasonId,
    isLastDepth: makeSelectIsLastDepth()
});

export default connect(mapStateToProps, {
    setColumnWidth,
    updateEntity,
    updateEntitiesValue,
    updateEntityValue
})(EntitiesVirtualizedTableComponent);
